import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import app from "./firebase";
import emailjs from "emailjs-com";
import { Helmet } from "react-helmet";
import { Leaf, ShieldCheck, ArrowRight, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const db = getFirestore(app);

const VerifyEmail = () => {
    const [, setCookie] = useCookies(["username", "name"]);
    const [otp, setOtp] = useState("");
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error'
    const [message, setMessage] = useState("");

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const emailParam = urlParams.get("email");
        if (emailParam) setEmail(emailParam);
        else window.location.href = "/login";
    }, []);

    const handleVerify = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setStatus("error");
            setMessage("Please enter the 6-digit code.");
            return;
        }

        setIsLoading(true);
        setStatus(null);
        try {
            const userid = email.replace("@", "").replace(".", "").toLowerCase();
            const userRef = doc(db, "users", userid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const userData = userSnap.data();
                if (userData.emailOTP === otp) {
                    await updateDoc(userRef, { emailVerified: true, emailOTP: "" });
                    sessionStorage.setItem("sessionActive", "1");
                    setCookie("username", userid, { path: '/' });
                    setCookie("name", userData.name || "User", { path: '/' });
                    setStatus("success");
                    setMessage("Email verified successfully! Redirecting...");
                    setTimeout(() => { window.location.href = "/market_dashboard"; }, 2000);
                } else {
                    setStatus("error");
                    setMessage("Invalid verification code. Please try again.");
                }
            } else {
                setStatus("error");
                setMessage("User account not found.");
            }
        } catch (error) {
            console.error(error);
            setStatus("error");
            setMessage("Verification failed. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (!email) {
            setStatus("error");
            setMessage("Email address is missing. Please try logging in again.");
            return;
        }
        setResending(true);
        try {
            const userid = email.replace("@", "").replace(".", "").toLowerCase();
            const userRef = doc(db, "users", userid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) {
                setStatus("error");
                setMessage("User account not found. Please register again.");
                return;
            }

            const userData = userSnap.data();
            const newOTP = Math.floor(100000 + Math.random() * 900000).toString();
            await updateDoc(userRef, { emailOTP: newOTP });

            const templateParams = {
                header: "AGRIVESTIA VERIFICATION",
                subject: "EMAIL VERIFICATION CODE",
                message: `Your new Agrivestia email verification code is: ${newOTP}`,
                email: email,
                name: userData.name || "Farmer"
            };
            await emailjs.send("service_pehhhv8", "template_rql77hn", templateParams, "9o1vflADP-wePeEFd");

            setStatus("success");
            setMessage("A new verification code has been sent to your email.");
            setTimeout(() => setStatus(null), 5000);
        } catch (error) {
            console.error("Resend error:", error);
            setStatus("error");
            setMessage("Failed to resend code. Please try again.");
        } finally {
            setResending(false);
        }
    };

    return (
        <div style={{ minHeight: "100vh", display: "flex", fontFamily: "var(--font-sans)", background: "var(--color-gray-50)" }}>
            <Helmet><title>Verify Your Email — Agrivestia</title></Helmet>

            {/* Left side: Brand info */}
            <div className="hide-mobile" style={{
                width: "40%", background: "var(--gradient-hero)",
                display: "flex", flexDirection: "column", justifyContent: "center",
                padding: "60px", position: "relative", overflow: "hidden"
            }}>
                <div style={{ position: "relative", zIndex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "40px" }}>
                        <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: "var(--gradient-accent)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Leaf size={20} color="white" />
                        </div>
                        <span style={{ fontWeight: 800, fontSize: "22px", color: "white" }}>AGRIVESTIA</span>
                    </div>

                    <h1 style={{ fontSize: "36px", fontWeight: 900, color: "white", lineHeight: 1.2, marginBottom: "20px" }}>
                        Verify Your <br />
                        <span style={{ color: "#86efac" }}>Email Address</span>
                    </h1>
                    <p style={{ fontSize: "16px", color: "rgba(255,255,255,0.7)", lineHeight: 1.6 }}>
                        We've sent a 6-digit verification code to <span style={{ color: "white", fontWeight: 600 }}>{email}</span>. Please enter it to activate your marketplace account.
                    </p>
                </div>
            </div>

            {/* Right side: Verification form */}
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px" }}>
                <div style={{ width: "100%", maxWidth: "400px", padding: "40px", background: "white", borderRadius: "var(--radius-xl)", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}>
                    <div style={{ textAlign: "center", marginBottom: "32px" }}>
                        <div style={{ display: "inline-flex", width: "64px", height: "64px", borderRadius: "20px", background: "var(--color-primary-light)", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
                            <ShieldCheck size={32} color="var(--color-primary)" />
                        </div>
                        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "var(--color-gray-900)" }}>Enter Code</h2>
                        <p style={{ fontSize: "14px", color: "var(--color-gray-500)", marginTop: "8px" }}>Please enter the verification code sent to your email.</p>
                    </div>

                    {status && (
                        <div style={{
                            display: "flex", alignItems: "center", gap: "12px", padding: "14px",
                            background: status === "success" ? "var(--color-success-light)" : "var(--color-danger-light)",
                            borderRadius: "var(--radius-md)", border: `1px solid ${status === "success" ? "#86efac" : "#fca5a5"}`,
                            marginBottom: "24px"
                        }}>
                            {status === "success" ? <CheckCircle2 size={18} color="#16a34a" /> : <AlertCircle size={18} color="#dc2626" />}
                            <p style={{ fontSize: "13px", color: status === "success" ? "#15803d" : "#991b1b", margin: 0, fontWeight: 500 }}>{message}</p>
                        </div>
                    )}

                    <form onSubmit={handleVerify}>
                        <div style={{ marginBottom: "24px" }}>
                            <input
                                type="text"
                                maxLength="6"
                                placeholder="000 000"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                                style={{
                                    width: "100%", textAlign: "center", letterSpacing: "12px", fontSize: "28px",
                                    fontWeight: 700, padding: "16px", borderRadius: "var(--radius-lg)",
                                    border: "2px solid var(--color-gray-200)", outline: "none",
                                    transition: "border-color 0.2s"
                                }}
                                onFocus={(e) => e.target.style.borderColor = "var(--color-primary)"}
                                onBlur={(e) => e.target.style.borderColor = "var(--color-gray-200)"}
                            />
                        </div>

                        <button type="submit" className="btn-primary" disabled={isLoading} style={{ width: "100%", justifyContent: "center", padding: "14px", fontSize: "16px" }}>
                            {isLoading ? "Verifying..." : <>Complete Verification <ArrowRight size={18} /></>}
                        </button>
                    </form>

                    <div style={{ marginTop: "32px", textAlign: "center" }}>
                        <p style={{ fontSize: "14px", color: "var(--color-gray-500)" }}>Didn't receive the code?</p>
                        <button
                            onClick={handleResend}
                            disabled={resending}
                            style={{
                                background: "none", border: "none", color: "var(--color-primary)",
                                fontWeight: 700, cursor: "pointer", display: "inline-flex",
                                alignItems: "center", gap: "6px", marginTop: "8px", fontSize: "14px"
                            }}
                        >
                            <RefreshCw size={14} className={resending ? "spin" : ""} /> {resending ? "Sending..." : "Resend Code"}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @media (max-width: 768px) {
                    .hide-mobile { display: none !important; }
                }
                .spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default VerifyEmail;
